<template>
  <v-container fluid>
    <v-row justify="space-between" class="px-10">
      <v-col cols="12" sm="12" md="7" lg="6">
        <v-btn
          color="primary"
          icon
          size="20"
          class="text-center"
          @click="$router.go(-1)"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <p class="text-h6 text--secondary d-inline font-weight-bold">
          Latest Workouts
        </p>
      </v-col>
      
    </v-row>

    <v-row class="px-10 mt-4">
      <v-col cols="12" sm="12" md="2" class="d-flex align-center justify-start">
        <span class="mr-3 text--secondary">Enable Custom order</span>
        <v-switch
          v-model="customOrderEnabled"
          color="primary"
          hide-details
          @change="onCustomOrderToggle"
          style="margin: 0 !important;"
        ></v-switch>
      </v-col>
    </v-row>

    <v-row class="px-10 mt-4">
      <v-col cols="12">
        <div class="workouts-container">
          <div class="workouts-list" ref="workoutsList">
            <div
              v-for="(workout, index) in displayWorkouts"
              :key="index"
              class="workout-item"
              @click="editWorkout(index)"
            >
              <div class="workout-square">
                <img
                  v-if="workout.image"
                  :src="workout.image"
                  :alt="workout.name || 'Workout'"
                  class="workout-image"
                />
                <div v-else class="workout-placeholder">
                  <v-icon size="48" color="grey lighten-1">mdi-dumbbell</v-icon>
                  <span class="placeholder-text">Click to add workout</span>
                </div>
                <div v-if="!workout.isEmpty" class="workout-overlay">
                  <v-btn
                    icon
                    small
                    color="white"
                    class="edit-btn"
                    @click.stop="editWorkout(index)"
                  >
                    <v-icon size="20">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="white"
                    class="delete-btn"
                    @click.stop="deleteWorkout(index)"
                  >
                    <v-icon size="20">mdi-close</v-icon>
                  </v-btn>
                </div>
                <div v-if="!workout.isEmpty" class="workout-arrows-overlay">
                  <v-btn
                    icon
                    small
                    color="white"
                    class="move-btn move-left"
                    :disabled="index === 0"
                    @click.stop="moveWorkoutLeft(index)"
                  >
                    <v-icon size="20">mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="white"
                    class="move-btn move-right"
                    :disabled="index >= displayWorkouts.length - 1"
                    @click.stop="moveWorkoutRight(index)"
                  >
                    <v-icon size="20">mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
                <div v-if="workout.name" class="workout-name-overlay">
                  <div class="workout-name">{{ workout.name }}</div>
                  <div class="workout-type">{{ getWorkoutTypeLabel(workout.workoutType) }}</div>
                </div>
              </div>
            </div>
            <!-- Add button as empty placeholder square -->
            <div
              class="workout-item"
              @click="addWorkout"
            >
              <div class="workout-square workout-add-square">
                <div class="workout-placeholder">
                  <v-icon size="48" color="grey lighten-1">mdi-dumbbell</v-icon>
                  <span class="placeholder-text">Click to add workout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Workout Selection Dialog -->
    <v-dialog v-model="dialog.show" max-width="800" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">Select Workout</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="dialog.workoutType"
            :items="workoutTypes"
            label="Filter by Workout Type (Optional)"
            outlined
            dense
            clearable
            @change="onWorkoutTypeChange"
            class="mb-4"
          ></v-select>
          
          <v-autocomplete
            v-model="dialog.selectedWorkout"
            :items="filteredWorkoutList"
            :loading="loadingWorkouts"
            :search-input.sync="searchQuery"
            item-text="displayName"
            item-value="id"
            return-object
            label="Search and select workout"
            placeholder="Type to search workouts..."
            outlined
            dense
            hide-selected
            clearable
            @change="onWorkoutSelected"
          >
            <template v-slot:item="data">
              <v-list-item-avatar>
                <img
                  v-if="data.item.image"
                  :src="data.item.image"
                  :alt="data.item.displayName"
                />
                <v-icon v-else color="grey">mdi-dumbbell</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ data.item.displayName }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ data.item.typeLabel }} • ID: {{ data.item.id }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="data">
              <v-chip
                v-if="data.item"
                :color="getWorkoutTypeColor(data.item.workoutType)"
                class="ma-2"
                small
              >
                <v-avatar left>
                  <img
                    v-if="data.item.image"
                    :src="data.item.image"
                    :alt="data.item.displayName"
                  />
                  <v-icon v-else small>mdi-dumbbell</v-icon>
                </v-avatar>
                {{ data.item.displayName }}
              </v-chip>
            </template>
          </v-autocomplete>

          <div v-if="dialog.workoutPreview" class="workout-preview mt-4">
            <div class="preview-image">
              <img
                v-if="dialog.workoutPreview.image"
                :src="dialog.workoutPreview.image"
                alt="Workout Preview"
              />
              <v-icon v-else size="64" color="grey">mdi-dumbbell</v-icon>
            </div>
            <div class="preview-name">{{ dialog.workoutPreview.name }}</div>
            <div class="preview-id">ID: {{ dialog.workoutPreview.id }}</div>
            <div class="preview-type">{{ dialog.workoutPreview.typeLabel }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveWorkout"
            :disabled="!dialog.workoutPreview"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import { getFirestore, doc, setDoc } from "firebase/firestore";

export default {
  name: "LatestWorkouts",
  components: {},
  data: () => ({
    loading: false,
    customOrderEnabled: false,
    dialog: {
      show: false,
      workoutType: "",
      selectedWorkout: null,
      workoutPreview: null,
      editingIndex: null,
    },
    workoutTypes: [
      { text: "Coached Class", value: "coachedClass" },
      { text: "Live", value: "live" },
      { text: "Pre Set", value: "preSet" },
    ],
    workoutList: [],
    loadingWorkouts: false,
    searchQuery: "",
    snackbar: {
      show: false,
      text: "",
      color: "success",
      timeout: 3000,
    },
  }),
  computed: {
    ...mapGetters(["getLatestWorkouts", "getProTrainersList", "getPreSets", "getLivesList"]),
    displayWorkouts() {
      const workouts = this.getLatestWorkouts || [];
      return workouts;
    },
    filteredWorkoutList() {
      let list = this.workoutList;
      
      // Filter by workout type if selected
      if (this.dialog.workoutType) {
        list = list.filter(w => w.workoutType === this.dialog.workoutType);
      }
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        list = list.filter(w => 
          w.displayName.toLowerCase().includes(query) ||
          w.id.toLowerCase().includes(query)
        );
      }
      
      return list;
    },
  },
  created() {
    this.loadWorkouts();
    this.loadAllWorkouts();
  },
  methods: {
    moment: function (value) {
      return moment(value);
    },
    async loadWorkouts() {
      this.loading = true;
      try {
        const result = await this.$store.dispatch("GetLatestWorkouts");
        if (result && result.customOrderEnabled !== undefined) {
          this.customOrderEnabled = result.customOrderEnabled;
        }
      } catch (error) {
        console.error("Error loading workouts:", error);
        this.showSnackbar("Error loading workouts", "error");
      } finally {
        this.loading = false;
      }
    },
    async onCustomOrderToggle() {
      try {
        const latestWorkoutsRef = doc(getFirestore(), "appConfiguration", "latestWorkouts");
        await setDoc(latestWorkoutsRef, {
          customOrderEnabled: this.customOrderEnabled,
          updatedAt: new Date()
        }, { merge: true });
        this.showSnackbar("Custom order setting updated", "success");
      } catch (error) {
        console.error("Error updating custom order setting:", error);
        this.showSnackbar("Error updating custom order setting", "error");
        // Revert the switch if update failed
        this.customOrderEnabled = !this.customOrderEnabled;
      }
    },
    async loadAllWorkouts() {
      this.loadingWorkouts = true;
      try {
        // Load all workout types
        await Promise.all([
          this.loadCoachedClasses(),
          this.loadLives(),
          this.loadPreSets(),
        ]);
      } catch (error) {
        console.error("Error loading workouts:", error);
        this.showSnackbar("Error loading workouts", "error");
      } finally {
        this.loadingWorkouts = false;
      }
    },
    async loadCoachedClasses() {
      try {
        const proTrainers = await this.$store.dispatch("GetProTrainersList");
        const coachedClasses = proTrainers.map(item => ({
          id: item.id,
          workoutId: item.id,
          workoutType: "coachedClass",
          displayName: item.name || "Coached Class",
          name: item.name || "Coached Class",
          image: item.selectedImagePreview?.link || "",
          typeLabel: "Coached Class",
        }));
        this.workoutList = this.workoutList.concat(coachedClasses);
      } catch (error) {
        console.error("Error loading coached classes:", error);
      }
    },
    async loadLives() {
      try {
        await this.$store.dispatch("GetLivesList");
        const lives = this.getLivesList || [];
        const livesList = lives.map(item => ({
          id: item.id,
          workoutId: item.id,
          workoutType: "live",
          displayName: item.title?.ENG || item.title || "Live",
          name: item.title?.ENG || item.title || "Live",
          image: item.image?.link || "",
          typeLabel: "Live",
        }));
        this.workoutList = this.workoutList.concat(livesList);
      } catch (error) {
        console.error("Error loading lives:", error);
      }
    },
    async loadPreSets() {
      try {
        const preSets = await this.$store.dispatch("GetPreSets");
        const preSetsList = preSets.map(item => ({
          id: item.id,
          workoutId: item.id,
          workoutType: "preSet",
          displayName: item.name?.ENG || item.name || "Pre Set",
          name: item.name?.ENG || item.name || "Pre Set",
          image: item.image?.link || item.image || "",
          typeLabel: "Pre Set",
        }));
        this.workoutList = this.workoutList.concat(preSetsList);
      } catch (error) {
        console.error("Error loading pre-sets:", error);
      }
    },
    addWorkout() {
      this.dialog = {
        show: true,
        workoutType: "",
        selectedWorkout: null,
        workoutPreview: null,
        editingIndex: null,
      };
      this.searchQuery = "";
    },
    editWorkout(index) {
      const workouts = this.getLatestWorkouts || [];
      
      if (!workouts[index]) {
        return;
      }
      
      const workout = workouts[index];
      // Find the workout in the list
      const foundWorkout = this.workoutList.find(
        w => w.workoutId === workout.workoutId && w.workoutType === workout.workoutType
      );
      
      this.dialog = {
        show: true,
        workoutType: workout.workoutType || "",
        selectedWorkout: foundWorkout || null,
        workoutPreview: workout,
        editingIndex: index,
      };
      this.searchQuery = "";
    },
    onWorkoutSelected(workout) {
      if (workout) {
        this.dialog.workoutPreview = {
          id: workout.id,
          workoutId: workout.workoutId,
          workoutType: workout.workoutType,
          image: workout.image,
          name: workout.name,
          typeLabel: workout.typeLabel,
        };
      } else {
        this.dialog.workoutPreview = null;
      }
    },
    getWorkoutTypeColor(type) {
      const colors = {
        coachedClass: "primary",
        live: "secondary",
        preSet: "accent",
      };
      return colors[type] || "grey";
    },
    getWorkoutTypeLabel(type) {
      const labels = {
        coachedClass: "Coached Class",
        live: "Live",
        preSet: "Pre Set",
      };
      return labels[type] || "Workout";
    },
    async saveWorkout() {
      if (!this.dialog.workoutPreview) {
        return;
      }

      try {
        const workouts = [...(this.getLatestWorkouts || [])];
        
        if (this.dialog.editingIndex !== null) {
          // Update existing workout
          workouts[this.dialog.editingIndex] = this.dialog.workoutPreview;
        } else {
          // Add new workout
          workouts.push(this.dialog.workoutPreview);
        }

        await this.$store.dispatch("UpdateLatestWorkouts", workouts);
        this.showSnackbar("Workout saved successfully", "success");
        this.closeDialog();
      } catch (error) {
        console.error("Error saving workout:", error);
        this.showSnackbar("Error saving workout", "error");
      }
    },
    async deleteWorkout(index) {
      const workouts = this.getLatestWorkouts || [];
      
      if (workouts[index]) {
        const updatedWorkouts = workouts.filter((_, i) => i !== index);
        try {
          await this.$store.dispatch("UpdateLatestWorkouts", updatedWorkouts);
          this.showSnackbar("Workout deleted successfully", "success");
        } catch (error) {
          console.error("Error deleting workout:", error);
          this.showSnackbar("Error deleting workout", "error");
        }
      }
    },
    async moveWorkoutLeft(index) {
      if (index === 0) return;
      
      const workouts = [...(this.getLatestWorkouts || [])];
      if (index > workouts.length - 1) return;
      
      // Swap with previous item
      [workouts[index - 1], workouts[index]] = [workouts[index], workouts[index - 1]];
      
      try {
        await this.$store.dispatch("UpdateLatestWorkouts", workouts);
        this.showSnackbar("Workout order updated", "success");
      } catch (error) {
        console.error("Error moving workout:", error);
        this.showSnackbar("Error updating workout order", "error");
      }
    },
    async moveWorkoutRight(index) {
      const workouts = [...(this.getLatestWorkouts || [])];
      if (index >= workouts.length - 1) return;
      
      // Swap with next item
      [workouts[index], workouts[index + 1]] = [workouts[index + 1], workouts[index]];
      
      try {
        await this.$store.dispatch("UpdateLatestWorkouts", workouts);
        this.showSnackbar("Workout order updated", "success");
      } catch (error) {
        console.error("Error moving workout:", error);
        this.showSnackbar("Error updating workout order", "error");
      }
    },
    onWorkoutTypeChange() {
      // Reset selection and preview when type changes
      this.dialog.selectedWorkout = null;
      this.dialog.workoutPreview = null;
      this.searchQuery = "";
    },
    closeDialog() {
      this.dialog = {
        show: false,
        workoutType: "",
        selectedWorkout: null,
        workoutPreview: null,
        editingIndex: null,
      };
      this.searchQuery = "";
    },
    showSnackbar(text, color = "success") {
      this.snackbar = {
        show: true,
        text: text,
        color: color,
        timeout: 3000,
      };
    },
  },
};
</script>

<style scoped>
.workouts-container {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
}

.workouts-list {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  flex: 1;
  padding: 8px 0;
  scrollbar-width: thin;
}

.workouts-list::-webkit-scrollbar {
  height: 8px;
}

.workouts-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.workouts-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.workouts-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.workout-item {
  flex-shrink: 0;
  cursor: pointer;
}

.workout-square {
  width: 300px;
  height: 200px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e0e0e0;
  transition: all 0.3s ease;
  background-color: #f5f5f5;
}

.workout-square:hover {
  border-color: #1976d2;
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.workout-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.workout-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  color: #9e9e9e;
}

.placeholder-text {
  margin-top: 8px;
  font-size: 12px;
  text-align: center;
  padding: 0 16px;
}

.workout-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  gap: 8px;
}

.workout-square:hover .workout-overlay {
  opacity: 1;
}

.workout-arrows-overlay {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;
}

.workout-square:hover .workout-arrows-overlay {
  opacity: 1;
}

.move-btn {
  background-color: rgba(76, 175, 80, 0.9) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
}

.move-btn:hover:not(:disabled) {
  background-color: rgba(76, 175, 80, 1) !important;
  transform: scale(1.1);
}

.move-btn:disabled {
  background-color: rgba(158, 158, 158, 0.5) !important;
  opacity: 0.5;
  cursor: not-allowed;
}

.edit-btn {
  background-color: rgba(33, 150, 243, 0.9) !important;
}

.edit-btn:hover {
  background-color: rgba(33, 150, 243, 1) !important;
}

.delete-btn {
  background-color: rgba(244, 67, 54, 0.9) !important;
}

.delete-btn:hover {
  background-color: rgba(244, 67, 54, 1) !important;
}

.workout-name-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  color: white;
  padding: 12px 8px 8px;
  text-align: center;
}

.workout-name {
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.workout-type {
  font-size: 11px;
  font-weight: 400;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.workout-add-square {
  cursor: pointer;
}

.workout-add-square:hover {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

.workout-preview {
  text-align: center;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.preview-image {
  margin-bottom: 12px;
}

.preview-image img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
}

.preview-name {
  font-size: 16px;
  font-weight: bold;
  color: #3a3a3a;
  margin-bottom: 4px;
}

.preview-id {
  font-size: 12px;
  color: #666;
}

.preview-type {
  font-size: 12px;
  color: #999;
  font-style: italic;
}
</style>
